import {
  isFollowUpInProgress,
  isSubtaskRunning,
} from '@littlebird/action-cards';

import type { WaitingForYouState } from '@/features/home/use-waiting-for-you';

import type { FeedItem } from './feed-search';
import type { FeedStatus } from '@littlebird/sdk';

export type FeedEntry = {
  item: FeedItem;
  status: FeedStatus;
  snoozed?: boolean;
};

export type FeedFamilyRows = {
  readonly parent: FeedEntry;
  readonly subtasks: readonly FeedEntry[];
};

/**
 * A parent's entry without its subtasks, and each open subtask as an entry of its own. A subtask's status is its
 * own, so a running subtask shows as in progress under a parent that waits on the user.
 */
export const splitSubtaskRows = (entry: FeedEntry): FeedFamilyRows => {
  const { item } = entry;
  if (item.kind !== 'provider' || !item.entry.children)
    return { parent: entry, subtasks: [] };
  return {
    parent: {
      ...entry,
      item: { ...item, entry: { ...item.entry, children: undefined } },
    },
    subtasks: item.entry.children.open.map((child) => ({
      item: { kind: 'provider', id: child.card.id, entry: child },
      status: isSubtaskRunning(child.card, isFollowUpInProgress)
        ? 'in_progress'
        : 'waiting',
    })),
  };
};

// A row is open while it, or a subtask it shows, is open. A parent split from its subtasks shows none
export const isFeedRowOpen = (
  home: Pick<WaitingForYouState, 'isItemExpanded' | 'isFamilyExpanded'>,
  item: FeedItem
): boolean =>
  item.kind === 'provider' && item.entry.children
    ? home.isFamilyExpanded(item.id)
    : home.isItemExpanded(item.id);
